import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from 'lucide-react';
import { useManualSubmit, useMinigameBackground } from '../hooks';
import { SuccessEffect, FailureEffect } from '../../effects';
import { Icon } from '../../foundation/primitives';
import { FailOverlay, JudgementModal, MinigameHud } from '../../foundation/semantic';

const Template09IntenseChase = ({ hostSubmit, hostConfig, hostMeta, registryId = 'minigame_quickcipher_09', templateId = 9 }) => {
  const config = hostConfig ?? {};
  const resolvedConfig = useMemo(() => {
    const safeNumber = (value, fallback) => (Number.isFinite(value) ? value : fallback);
    const pickText = (value, fallback) => (typeof value === 'string' && value.trim() !== '' ? value : fallback);
    const failMessage = typeof config.failMessage === 'object' && config.failMessage !== null ? config.failMessage : {};
    return {
      defaultMode: config.defaultMode === 'escape' ? 'escape' : 'chase',
      allowModeSelect: config.allowModeSelect !== false,
      maxRounds: safeNumber(config.maxRounds, 5),
      initialDistance: safeNumber(config.initialDistance, 50),
      successDistance: safeNumber(config.successDistance, 100),
      failDistance: safeNumber(config.failDistance, 0),
      roundTimeLimitMs: safeNumber(config.roundTimeLimitMs, 2000),
      countdownMs: safeNumber(config.countdownMs, 800),
      correctBonus: safeNumber(config.correctBonus, 15),
      wrongPenalty: safeNumber(config.wrongPenalty, 10),
      timeoutPenalty: safeNumber(config.timeoutPenalty, 10),
      directions: Array.isArray(config.directions) && config.directions.length > 0 ? config.directions : ['up', 'down', 'left', 'right'],
      directionColors: typeof config.directionColors === 'object' && config.directionColors ? config.directionColors : {},
      roundMessages: Array.isArray(config.roundMessages) ? config.roundMessages : [],
      // R1 体験文法: モード選択画面（実質の開始ゲート）と HUD の見出し・説明（config 優先・既定は常体）
      title: pickText(config.title, '緊迫チェイス'),
      instruction: pickText(config.instructionText, '示された方向を素早く押せ。反応の速さが勝負を決める'),
      // R1 体験文法: FailOverlay の文言（config.failMessage 優先。null = mode 別の既定文言を使う）
      failTitle: pickText(failMessage.title, null),
      failSubtitle: pickText(failMessage.subtitle, null),
      allowRetry: failMessage.allowRetry !== false
    };
  }, [config]);
  const backgroundStyle = useMinigameBackground(hostConfig);
  const [mode, setMode] = useState(resolvedConfig.defaultMode);
  const [round, setRound] = useState(0);
  const [maxRounds, setMaxRounds] = useState(resolvedConfig.maxRounds);
  const [distance, setDistance] = useState(resolvedConfig.initialDistance);
  const [gameState, setGameState] = useState('ready');
  const [currentDirection, setCurrentDirection] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [maxTime, setMaxTime] = useState(resolvedConfig.roundTimeLimitMs);
  const [roundResult, setRoundResult] = useState(null);
  const [selectedDirection, setSelectedDirection] = useState(null);
  const [showEffect, setShowEffect] = useState(null);
  // R1 体験文法: FailOverlay「やり直す」の回数。全 submit payload に載せる
  const [retryCount, setRetryCount] = useState(0);
  const { submit, resetSubmission } = useManualSubmit(registryId, templateId, hostSubmit);

  const defaultDirectionColors = {
    up: '#f59e0b',
    down: '#38bdf8',
    left: '#a855f7',
    right: '#22c55e'
  };
  const directionMeta = {
    up: { icon: ArrowUp, label: '↑' },
    down: { icon: ArrowDown, label: '↓' },
    left: { icon: ArrowLeft, label: '←' },
    right: { icon: ArrowRight, label: '→' }
  };
  const directions = useMemo(
    () =>
      resolvedConfig.directions.map((id) => ({
        id,
        icon: directionMeta[id]?.icon ?? ArrowUp,
        label: directionMeta[id]?.label ?? id,
        color: resolvedConfig.directionColors?.[id] ?? defaultDirectionColors[id] ?? '#94a3b8'
      })),
    [resolvedConfig.directions, resolvedConfig.directionColors]
  );
  const availableDirections = useMemo(() => new Set(resolvedConfig.directions), [resolvedConfig.directions]);

  useEffect(() => {
    setMode(resolvedConfig.defaultMode);
    setMaxRounds(resolvedConfig.maxRounds);
    setDistance(resolvedConfig.initialDistance);
    setMaxTime(resolvedConfig.roundTimeLimitMs);
    setRound(0);
    setGameState('ready');
    setCurrentDirection(null);
    setRoundResult(null);
    setSelectedDirection(null);
    resetSubmission();
  }, [resolvedConfig]);

  const startRound = useCallback(() => {
    const randomDir = directions[Math.floor(Math.random() * directions.length)];
    setCurrentDirection(randomDir);
    setTimeLeft(maxTime);
    setSelectedDirection(null);
    setRoundResult(null);
    setGameState('playing');
  }, [directions, maxTime]);

  useEffect(() => {
    if (gameState === 'countdown') {
      const timer = setTimeout(() => {
        startRound();
      }, resolvedConfig.countdownMs);
      return () => clearTimeout(timer);
    }
  }, [gameState, startRound, resolvedConfig.countdownMs]);

  useEffect(() => {
    if (gameState !== 'playing') return;

    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 50) {
          handleResult(false, 'timeout');
          return 0;
        }
        return prev - 50;
      });
    }, 50);

    return () => clearInterval(interval);
  }, [gameState]);

  const handleDirectionSelect = (dirId) => {
    if (gameState !== 'playing' || selectedDirection) return;

    setSelectedDirection(dirId);
    const isCorrect = dirId === currentDirection.id;
    handleResult(isCorrect);
  };

  const handleResult = (isSuccess, reason = 'choice') => {
    setGameState('result');
    setRoundResult(isSuccess ? 'success' : 'fail');
    setShowEffect(isSuccess ? 'round_success' : 'round_fail');

    let newDistance = distance;
    if (mode === 'chase') {
      const penalty = reason === 'timeout' ? resolvedConfig.timeoutPenalty : resolvedConfig.wrongPenalty;
      newDistance = isSuccess ? distance + resolvedConfig.correctBonus : distance - penalty;
    } else {
      const penalty = reason === 'timeout' ? resolvedConfig.timeoutPenalty : resolvedConfig.wrongPenalty;
      newDistance = isSuccess ? distance - resolvedConfig.correctBonus : distance + penalty;
    }
    const minDistance = Math.min(resolvedConfig.failDistance, resolvedConfig.successDistance);
    const maxDistance = Math.max(resolvedConfig.failDistance, resolvedConfig.successDistance);
    newDistance = Math.max(minDistance, Math.min(maxDistance, newDistance));
    setDistance(newDistance);

    setTimeout(() => {
      if (mode === 'chase' && newDistance >= resolvedConfig.successDistance) {
        setShowEffect('success');
        setGameState('success');
      } else if (mode === 'chase' && newDistance <= resolvedConfig.failDistance) {
        setShowEffect('fail');
        setGameState('fail');
      } else if (mode === 'escape' && newDistance <= resolvedConfig.failDistance) {
        setShowEffect('success');
        setGameState('success');
      } else if (mode === 'escape' && newDistance >= resolvedConfig.successDistance) {
        setShowEffect('fail');
        setGameState('fail');
      } else if (round + 1 >= maxRounds) {
        const midpoint = (resolvedConfig.successDistance + resolvedConfig.failDistance) / 2;
        if (mode === 'chase') {
          const finalResult = newDistance >= midpoint ? 'success' : 'fail';
          setShowEffect(finalResult);
          setGameState(finalResult);
        } else {
          const finalResult = newDistance <= midpoint ? 'success' : 'fail';
          setShowEffect(finalResult);
          setGameState(finalResult);
        }
      } else {
        setShowEffect(null);
        setRound(r => r + 1);
        setGameState('countdown');
      }
    }, 1000);
  };

  const startGame = (selectedMode) => {
    setMode(selectedMode);
    setRound(0);
    setDistance(resolvedConfig.initialDistance);
    setGameState('countdown');
  };

  const reset = () => {
    setRound(0);
    // R1 修理: 旧ハードコード 50 → config 駆動の initialDistance（既定 50 のままなのでゼロ config 回帰なし）
    setDistance(resolvedConfig.initialDistance);
    setGameState('ready');
    setCurrentDirection(null);
    setRoundResult(null);
    setSelectedDirection(null);
    setShowEffect(null);
    resetSubmission();
  };

  // FailOverlay「やり直す」: 内部リセット（モード選択から再開）+ retryCount 加算
  const handleRetry = () => {
    reset();
    setRetryCount((count) => count + 1);
  };

  // R1 体験文法: 距離・ラウンド HUD（manifest 宣言済み distance-tension-track の実装）。
  // 内部 state の distance は「追う側がどれだけ迫っているか（接近度）」のゲージ値
  // （chase: 増えるほど確保に近い / escape: 増えるほど捕まりに近い）なので、
  // 表示名は「距離」でなく「接近」とし、数値+小ゲージで両者の間合いを可視化する。
  // 段階色は勝利側への進み（winProgress）基準: >70% safe / >30% warn / それ以下 danger（T08 ゲージと同じ閾値）。
  const distanceFloor = Math.min(resolvedConfig.failDistance, resolvedConfig.successDistance);
  const distanceCeil = Math.max(resolvedConfig.failDistance, resolvedConfig.successDistance);
  const distancePercent = distanceCeil === distanceFloor
    ? 50
    : Math.min(100, Math.max(0, ((distance - distanceFloor) / (distanceCeil - distanceFloor)) * 100));
  const winTarget = mode === 'chase' ? resolvedConfig.successDistance : resolvedConfig.failDistance;
  const loseTarget = mode === 'chase' ? resolvedConfig.failDistance : resolvedConfig.successDistance;
  const winProgress = winTarget === loseTarget
    ? 0.5
    : Math.min(1, Math.max(0, (distance - loseTarget) / (winTarget - loseTarget)));
  const distanceStageClass =
    winProgress > 0.7 ? 't09-hud-fill--safe' : winProgress > 0.3 ? 't09-hud-fill--warn' : 't09-hud-fill--danger';
  // ラウンド番号は 1 始まりで表示（round は 0 始まり。result 中は今やり終えたラウンド番号のまま）
  const displayRound = Math.min(round + 1, maxRounds);
  const hudVisible = gameState === 'countdown' || gameState === 'playing' || gameState === 'result';

  // config 駆動の方向色マッピング（hostConfig.directionColors の hex を温存する data-driven 描画。
  // ここは config 抽出と同じ「変えない側」: 既定色・named 色クラス・hex パススルーを一切変えない）。
  const getDirectionStyle = (dir) => {
    const colors = {
      blue: 'from-blue-500 to-blue-600',
      green: 'from-green-500 to-green-600',
      amber: 'from-amber-500 to-amber-600',
      purple: 'from-purple-500 to-purple-600'
    };
    if (typeof dir.color === 'string' && dir.color.trim().startsWith('#')) {
      return { className: 'from-gray-500 to-gray-600', style: { background: `linear-gradient(135deg, ${dir.color}, ${dir.color})` } };
    }
    return { className: colors[dir.color] || 'from-gray-500 to-gray-600', style: undefined };
  };

  return (
    <div className="t09-root relative w-full h-full overflow-hidden" style={backgroundStyle ?? undefined}>
      {!backgroundStyle && (
        <div
          className={`absolute inset-0 transition-all duration-300 ${
            gameState === 'playing' ? 't09-bg--active' : 't09-bg--idle'
          }`}
        />
      )}

      {gameState === 'playing' && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="t09-alert-frame absolute inset-0 border-4 animate-pulse" />
        </div>
      )}

      {/* R1 体験文法: 距離・ラウンド HUD（監査指摘: state はあるのに HUD 未描画、を解消）。
          rightSlot = 「ラウンド n/N」チップ + 接近度（数値+小ゲージ= distance-tension-track）。
          ラウンド間で点滅しないよう countdown を含むプレイ中フェーズで出し続ける
          （ready とゲーム終了時は出さない）。円形カウントダウンリング等の演出は不変 */}
      {hudVisible && (
        <MinigameHud
          title={resolvedConfig.title}
          icon="Zap"
          rightSlot={
            <>
              <span className="mg-hud__chip">ラウンド {displayRound}/{maxRounds}</span>
              <span className="mg-hud__chip t09-hud-distance" aria-label={`接近度 ${Math.round(distance)}`}>
                接近 {Math.round(distance)}
                <span className="t09-hud-track" aria-hidden>
                  <span className={`t09-hud-fill ${distanceStageClass}`} style={{ width: `${distancePercent}%` }} />
                </span>
              </span>
            </>
          }
        />
      )}

      <div className="absolute top-36 left-0 right-0 bottom-4 flex items-center justify-center px-4">

        {gameState === 'ready' && (
          <div className="text-center">
            {/* R1 体験文法: モード選択が実質の開始ゲートなので StartGate は被せず、
                タイトル+説明一行をこの画面に添える（showTutorial===false なら旧来の素のモード選択に戻る） */}
            {hostMeta?.ui?.showTutorial !== false && (
              <>
                <h2 className="t09-gate-title text-xl font-bold mb-2">{resolvedConfig.title}</h2>
                <p className="t09-gate-instruction text-sm mb-6">{resolvedConfig.instruction}</p>
              </>
            )}
            <div className="space-y-3">
              {resolvedConfig.allowModeSelect ? (
                <>
                  <button
                    className="t09-mode-btn t09-mode-btn--chase w-full font-bold py-4 rounded-xl flex items-center justify-center gap-2"
                    onClick={() => startGame('chase')}
                  >
                    <span className="text-2xl">🔥</span>
                    追いかける側
                  </button>
                  <button
                    className="t09-mode-btn t09-mode-btn--escape w-full font-bold py-4 rounded-xl flex items-center justify-center gap-2"
                    onClick={() => startGame('escape')}
                  >
                    <span className="text-2xl">💨</span>
                    逃げる側
                  </button>
                </>
              ) : (
                <button
                  className={`t09-mode-btn w-full font-bold py-4 rounded-xl flex items-center justify-center gap-2 ${
                    resolvedConfig.defaultMode === 'escape'
                      ? 't09-mode-btn--escape'
                      : 't09-mode-btn--chase'
                  }`}
                  onClick={() => startGame(resolvedConfig.defaultMode)}
                >
                  <span className="text-2xl">{resolvedConfig.defaultMode === 'escape' ? '💨' : '🔥'}</span>
                  {resolvedConfig.defaultMode === 'escape' ? '逃げる側' : '追いかける側'}
                </button>
              )}
            </div>
          </div>
        )}

        {gameState === 'countdown' && (
          <div className="text-center">
            <div className="t09-countdown-badge w-24 h-24 mx-auto rounded-full flex items-center justify-center animate-pulse">
              <span className="text-4xl font-bold">!</span>
            </div>
            <p className="t09-countdown-text text-lg mt-4 font-bold">構えろ...</p>
          </div>
        )}

        {gameState === 'playing' && currentDirection && (
          <div className="w-full">
            <div className="text-center mb-4">
              {(() => {
                const style = getDirectionStyle(currentDirection);
                const timePercent = (timeLeft / maxTime) * 100;
                return (
                  <div className="relative inline-block">
                    <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                      <circle cx="40" cy="40" r="36" fill="none" className="t09-ring-track" strokeWidth="3" />
                      <circle
                        cx="40" cy="40" r="36" fill="none"
                        className={timePercent < 30 ? 't09-ring--danger' : timePercent < 60 ? 't09-ring--warn' : 't09-ring--safe'}
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeDasharray={`${2 * Math.PI * 36}`}
                        strokeDashoffset={`${2 * Math.PI * 36 * (1 - timePercent / 100)}`}
                        style={{ transition: 'stroke-dashoffset 50ms linear' }}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div
                        className="t09-prompt-tile w-12 h-12 rounded-xl flex items-center justify-center shadow-lg"
                        style={style.style ?? { background: `linear-gradient(135deg, ${currentDirection.color}, ${currentDirection.color}dd)` }}
                      >
                        <Icon icon={currentDirection.icon} size={28} className="t09-prompt-icon drop-shadow" />
                      </div>
                    </div>
                  </div>
                );
              })()}
              <p className="t09-prompt-hint text-xs mt-2">素早く押せ！</p>
            </div>

            <div className="grid grid-cols-3 gap-2 max-w-xs mx-auto">
              <div />
              <button
                className={`t09-pad-btn aspect-square rounded-xl flex items-center justify-center transition-all ${
                  selectedDirection === 'up'
                    ? 't09-pad-btn--sel-up scale-95'
                    : availableDirections.has('up')
                      ? 't09-pad-btn--idle active:scale-95'
                      : 't09-pad-btn--off'
                }`}
                onClick={() => handleDirectionSelect('up')}
                disabled={!availableDirections.has('up')}
              >
                <Icon icon={ArrowUp} size={32} className="t09-pad-icon" />
              </button>
              <div />

              <button
                className={`t09-pad-btn aspect-square rounded-xl flex items-center justify-center transition-all ${
                  selectedDirection === 'left'
                    ? 't09-pad-btn--sel-left scale-95'
                    : availableDirections.has('left')
                      ? 't09-pad-btn--idle active:scale-95'
                      : 't09-pad-btn--off'
                }`}
                onClick={() => handleDirectionSelect('left')}
                disabled={!availableDirections.has('left')}
              >
                <Icon icon={ArrowLeft} size={32} className="t09-pad-icon" />
              </button>
              <button
                className={`t09-pad-btn aspect-square rounded-xl flex items-center justify-center transition-all ${
                  selectedDirection === 'down'
                    ? 't09-pad-btn--sel-down scale-95'
                    : availableDirections.has('down')
                      ? 't09-pad-btn--idle active:scale-95'
                      : 't09-pad-btn--off'
                }`}
                onClick={() => handleDirectionSelect('down')}
                disabled={!availableDirections.has('down')}
              >
                <Icon icon={ArrowDown} size={32} className="t09-pad-icon" />
              </button>
              <button
                className={`t09-pad-btn aspect-square rounded-xl flex items-center justify-center transition-all ${
                  selectedDirection === 'right'
                    ? 't09-pad-btn--sel-right scale-95'
                    : availableDirections.has('right')
                      ? 't09-pad-btn--idle active:scale-95'
                      : 't09-pad-btn--off'
                }`}
                onClick={() => handleDirectionSelect('right')}
                disabled={!availableDirections.has('right')}
              >
                <Icon icon={ArrowRight} size={32} className="t09-pad-icon" />
              </button>
            </div>
          </div>
        )}

        {gameState === 'result' && (
          <div className="text-center">
            <div className={`t09-result-badge w-24 h-24 mx-auto rounded-full flex items-center justify-center ${
              roundResult === 'success' ? 't09-result-badge--ok' : 't09-result-badge--ng'
            }`}>
              <span className="text-4xl">
                {roundResult === 'success' ? '✓' : '✗'}
              </span>
            </div>
            <p className={`text-xl font-bold mt-4 ${
              roundResult === 'success' ? 't09-result-text--ok' : 't09-result-text--ng'
            }`}>
              {roundResult === 'success'
                ? (mode === 'chase' ? '距離を詰めた！' : '引き離した！')
                : (mode === 'chase' ? '離された！' : '追いつかれた！')
              }
            </p>
          </div>
        )}
      </div>

      {/* Foundation: 成功モーダル（中央バッジ + タイトル + 説明 + 次へ進む を JudgementModal に集約。
          mode で文言・絵文字バッジを切替。旧: green グラデ円バッジ + 独自 bg-black/85 オーバーレイ。
          submit('success') 呼び出しは不変） */}
      <JudgementModal
        open={gameState === 'success'}
        type="success"
        title={mode === 'chase' ? '確保成功！' : '逃走成功！'}
        description={mode === 'chase' ? '容疑者を追い詰めた' : '追手を振り切った'}
        icon={<span className="text-5xl">{mode === 'chase' ? '🔥' : '💨'}</span>}
        buttonLabel="次へ進む"
        onSubmit={() => submit('success', { retryCount })}
      />

      {/* R1 体験文法: 失敗を FailOverlay semantic へ移行（旧 .t09-fail-* 独自オーバーレイは撤去）。
          やり直す = reset + retryCount 加算 / 未整理のまま進む = submit('fail')。
          文言は config.failMessage を尊重し、不在時は mode 別の現行既定（常体） */}
      {gameState === 'fail' && (
        <FailOverlay
          title={resolvedConfig.failTitle ?? (mode === 'chase' ? '逃走された...' : '捕まった...')}
          subtitle={resolvedConfig.failSubtitle ?? (mode === 'chase' ? '容疑者を見失った' : '追手に捕まってしまった')}
          allowRetry={resolvedConfig.allowRetry}
          onRetry={handleRetry}
          onProceed={() => submit('fail', { retryCount })}
        />
      )}

      {showEffect === 'round_success' && (
        <SuccessEffect type="shine" duration={400} onComplete={() => {}} />
      )}
      {showEffect === 'round_fail' && (
        <FailureEffect type="flash" duration={300} onComplete={() => {}} />
      )}
      {showEffect === 'success' && (
        <SuccessEffect type="confetti" duration={1200} onComplete={() => setShowEffect(null)} />
      )}
      {showEffect === 'fail' && (
        <FailureEffect type="shake" duration={500} onComplete={() => setShowEffect(null)} />
      )}
    </div>
  );
};

export default Template09IntenseChase;
